import React, { createContext, useContext, useState, useCallback } from 'react';

const CartContext = createContext();

const UNIT_PRICE = 115;
const BULK_UNIT_PRICE = 110;
const BULK_THRESHOLD = 3;
const SHIPPING_FEE = 35;

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  const totalQty = items.reduce((sum, item) => sum + item.qty, 0);
  const isBulk = totalQty >= BULK_THRESHOLD;
  const unitPrice = isBulk ? BULK_UNIT_PRICE : UNIT_PRICE;
  const subtotal = totalQty * unitPrice;
  const shipping = isBulk ? 0 : SHIPPING_FEE;
  const total = subtotal + shipping;
  const unitsUntilBulk = isBulk ? 0 : BULK_THRESHOLD - totalQty;

  const addItem = useCallback((product) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.id === product.id);
      if (existing) {
        return prev.map((i) =>
          i.id === product.id ? { ...i, qty: i.qty + 1 } : i
        );
      }
      return [...prev, { ...product, qty: 1 }];
    });
    setIsOpen(true);
  }, []);

  const updateQty = useCallback((id, qty) => {
    if (qty <= 0) {
      setItems((prev) => prev.filter((i) => i.id !== id));
    } else {
      setItems((prev) =>
        prev.map((i) => (i.id === id ? { ...i, qty } : i))
      );
    }
  }, []);

  const removeItem = useCallback((id) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const value = {
    items,
    totalQty,
    isBulk,
    unitPrice,
    subtotal,
    shipping,
    total,
    unitsUntilBulk,
    isOpen,
    setIsOpen,
    addItem,
    updateQty,
    removeItem,
    UNIT_PRICE,
    BULK_UNIT_PRICE,
    BULK_THRESHOLD,
    SHIPPING_FEE,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
